"use client";
import { Modal } from "antd";
import React, { useState } from "react";

export interface AppModalProps {
  /** Modal heading */
  title?: React.ReactNode;
  /**
   * Element that opens the modal when clicked (e.g. a Button).
   * When provided, the modal manages its own open state internally
   * unless you also pass `open` + `onOpenChange`.
   */
  trigger?: React.ReactElement;
  /** Controlled open state */
  open?: boolean;
  /** Called when the modal should open or close (controlled mode) */
  onOpenChange?: (open: boolean) => void;
  width?: number | string;
  /**
   * Footer content. Pass `null` to hide it entirely.
   * Defaults to `null` (no built-in OK/Cancel buttons — forms supply their own).
   */
  footer?: React.ReactNode | null;
  destroyOnHidden?: boolean;
  /**
   * Lands on the modal root. The modal is portaled out of whatever rendered it,
   * so a caller's scoped `<style>` can only reach the content through a class
   * put here.
   */
  className?: string;
  /** Per-section style overrides (header / body / content / …). */
  styles?: React.ComponentProps<typeof Modal>["styles"];
  /**
   * Modal body. Accepts either static JSX or a render-prop that receives
   * a `close()` callback so the content can close the modal itself.
   */
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
}

export function AppModal({
  title,
  trigger,
  open: controlledOpen,
  onOpenChange,
  width,
  footer = null,
  destroyOnHidden = true,
  className,
  styles,
  children,
}: AppModalProps) {
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen! : internalOpen;

  const show = () => (isControlled ? onOpenChange?.(true) : setInternalOpen(true));
  const close = () => (isControlled ? onOpenChange?.(false) : setInternalOpen(false));

  return (
    <>
      {trigger && React.cloneElement(trigger as React.ReactElement<{ onClick?: () => void }>, { onClick: show })}
      <Modal
        closable
        open={open}
        title={title}
        width={width}
        footer={footer}
        onCancel={close}
        destroyOnHidden={destroyOnHidden}
        className={className}
        styles={styles}
      >
        {typeof children === "function" ? children(close) : children}
      </Modal>
    </>
  );
}
